import type ProductWizardController from "../product_wizard_controller";
import { URL_PARAM_FIELDS, VARIANT_FIELDS } from "./constants";
import { DecorSuiteFormsSearchableSelectIdentifier } from "decor";

/** Collect form field values that should be persisted to URL params. */
export function getFormValues(
  controller: ProductWizardController,
): Record<string, string> {
  const form = controller.element.querySelector("form");
  if (!form) return {};

  const values: Record<string, string> = {};
  for (const field of URL_PARAM_FIELDS) {
    const input = form.querySelector(`[name*="[${field}]"]`) as
      | HTMLInputElement
      | HTMLSelectElement
      | null;
    if (input?.value) {
      values[field] = input.value;
    }
  }
  return values;
}

/** Restore form field values from a key-value map. */
export function setFormValues(
  controller: ProductWizardController,
  values: Record<string, string>,
): void {
  const form = controller.element.querySelector("form");
  if (!form) return;

  for (const [field, value] of Object.entries(values)) {
    if (!value) continue;

    // First try: find a SearchableSelect component for this field
    if (setSearchableSelectValue(controller, field, value)) continue;

    // Fallback: set plain input/select value
    const input = form.querySelector(`[name*="[${field}]"]`) as
      | HTMLInputElement
      | HTMLSelectElement
      | null;
    if (input) {
      input.value = value;
      if (input.tagName === "SELECT") {
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }
}

/** Set a SearchableSelect component value by field name. Returns true if handled. */
export function setSearchableSelectValue(
  controller: ProductWizardController,
  fieldName: string,
  value: string,
): boolean {
  const components = controller.element.querySelectorAll(
    '[data-controller*="searchable-select"]:not([data-controller*="multi"])',
  );

  for (const component of components) {
    const fieldNameAttr = component.getAttribute(
      `data-${DecorSuiteFormsSearchableSelectIdentifier}-field-name-value`,
    );
    if (!fieldNameAttr || !fieldNameAttr.includes(`[${fieldName}]`)) continue;

    component.setAttribute(
      `data-${DecorSuiteFormsSearchableSelectIdentifier}-selected-item-value`,
      JSON.stringify({ id: value }),
    );
    return true;
  }

  return false;
}

/** Build URL search params from current step + form values + variant data. */
export function buildUrlParams(
  controller: ProductWizardController,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set("step", String(controller.currentStepValue));

  const values = getFormValues(controller);
  for (const [key, value] of Object.entries(values)) {
    if (value) params.set(key, value);
  }

  // Serialize variants
  const variants = controller.variantsContainerTarget.querySelectorAll(
    "[data-variant-entry]",
  );
  variants.forEach((variant, index) => {
    for (const field of VARIANT_FIELDS) {
      const el = variant.querySelector(`[name*="[${field}]"]`) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
      if (!el) continue;
      let val: string;
      if (el.type === "checkbox") {
        val = (el as HTMLInputElement).checked ? "true" : "";
      } else {
        val = el.value;
      }
      if (val) {
        params.set(`v[${index}][${field}]`, val);
      }
    }
  });

  return params;
}

/** Push a new browser history entry with current form state. */
export function pushUrlState(controller: ProductWizardController): void {
  const params = buildUrlParams(controller);
  const url = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({ step: controller.currentStepValue }, "", url);
}

/** Replace current browser history entry with current form state. */
export function replaceUrlState(controller: ProductWizardController): void {
  const params = buildUrlParams(controller);
  const url = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({ step: controller.currentStepValue }, "", url);
}

/** Debounced version of replaceUrlState (500ms). */
export function debouncedReplaceUrlState(
  controller: ProductWizardController,
): void {
  if (controller.urlUpdateTimer) clearTimeout(controller.urlUpdateTimer);
  controller.urlUpdateTimer = setTimeout(
    () => replaceUrlState(controller),
    500,
  );
}

/** Restore full wizard state (step, form values, variants) from URL params. */
export function restoreFromUrl(controller: ProductWizardController): void {
  const params = new URLSearchParams(window.location.search);

  // Restore step
  const step = parseInt(params.get("step") || "", 10);
  if (step >= 1 && step <= controller.totalStepsValue) {
    controller.currentStepValue = step;
  }

  // Restore form values
  const values: Record<string, string> = {};
  for (const field of URL_PARAM_FIELDS) {
    const value = params.get(field);
    if (value) values[field] = value;
  }

  if (Object.keys(values).length > 0) {
    setFormValues(controller, values);
  }

  // Restore variants from URL params (v[0][sell_uom]=CS&v[0][sell_weight]=10...)
  restoreVariantsFromUrl(controller, params);
}

/** Restore variant entries from URL search params. */
export function restoreVariantsFromUrl(
  controller: ProductWizardController,
  params: URLSearchParams,
): void {
  // Collect variant indices from params
  const variantData: Map<number, Record<string, string>> = new Map();
  params.forEach((value, key) => {
    const match = key.match(/^v\[(\d+)]\[(\w+)]$/);
    if (!match) return;
    const idx = parseInt(match[1], 10);
    const field = match[2];
    if (!variantData.has(idx)) variantData.set(idx, {});
    variantData.get(idx)![field] = value;
  });

  if (variantData.size === 0) return;

  // Sort by index and create variants
  const sorted = [...variantData.entries()].sort((a, b) => a[0] - b[0]);
  for (const [, fields] of sorted) {
    // Add a new variant using the template
    controller.addVariant();

    // Get the last added variant entry
    const entries = controller.variantsContainerTarget.querySelectorAll(
      "[data-variant-entry]",
    );
    const variantEl = entries[entries.length - 1];
    if (!variantEl) continue;

    // Fill in the fields
    for (const [field, value] of Object.entries(fields)) {
      const el = variantEl.querySelector(`[name*="[${field}]"]`) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
      if (!el) continue;

      if (el.type === "checkbox") {
        (el as HTMLInputElement).checked = value === "true";
      } else {
        el.value = value;
        el.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }
}

/** Handle browser back/forward navigation. */
export function handlePopState(
  controller: ProductWizardController,
  _event: PopStateEvent,
): void {
  const params = new URLSearchParams(window.location.search);
  const step = parseInt(params.get("step") || "1", 10);

  if (step >= 1 && step <= controller.totalStepsValue) {
    controller.currentStepValue = step;
    controller.showCurrentStep();
    controller.updateNavButtons();
    controller.clearStepError();

    // Restore form values from URL
    const values: Record<string, string> = {};
    for (const field of URL_PARAM_FIELDS) {
      const value = params.get(field);
      if (value) values[field] = value;
    }
    if (Object.keys(values).length > 0) {
      setFormValues(controller, values);
      controller.updatePreview();
    }

    // Restore variants (clear first to avoid duplicates)
    controller.variantsContainerTarget.innerHTML = "";
    restoreVariantsFromUrl(controller, params);
    controller.updateVariantCount();
  }
}
